import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useAuth } from '@/middlewares/auth/auth-context'
import { hasConsent } from '@/components/CookiesConsent/index.utils'
import { WELCOME_DONE_EVENT, isWelcomeSeen } from '@/components/DesignTransition'
import { DASHBOARD_TOUR } from './definitions/dashboard.tour'
import { markAutoAttempted, readAutoAttempted } from './tourProgress.storage'
import { tourForPath, tourForRoute } from './tours.registry'
import { isDialogOpen, waitForTarget } from './tourTargets'
import useTourStore from './tourStore'

const SETTLE_MS = 600
const TARGET_WAIT_MS = 4000

const firstTargetOf = (def, fallback) => def.steps.find((step) => step.target)?.target || fallback

/**
 * The guided tours' first contact: an automatic start at the first visit of a page, then the invitation
 * card (William, 29 Oct 2026: « lancer les guides automatiquement à la première visite de chaque page »,
 * and « au chargement d'une page organisateur, afficher une petite pop up en haut à droite proposant de
 * voir le guide de cette page »; asked which comes after the first visit, he chose the card).
 *  - first visit = no trace of the tour on the account nor in the local mirror (`isDone` false: never
 *    completed, skipped nor dismissed at this version): the tour starts by itself once the page settled;
 *  - afterwards the card offers it, and the organizer answers: « See the guide » starts it and leaves
 *    the card free to come back at the next page load (launching the tour is not an answer); « Not now »
 *    hides the card for this browser session (`sf-tour-auto:<id>`), it comes back next time; « Don't
 *    show again (for this page) » records a dismissal on the account (`dismissedAt`): the card never
 *    comes back for that version of the tour, the help menu still offers it with its dot.
 * The card comes back at every page load otherwise (William's rule of 29 Oct 2026, « à chaque
 * chargement »): only the dismissal and the session marker stop it.
 * Which tour, once the cookies banner is answered, the welcome card closed, no tour playing, no dialog
 * open and the first target painted:
 *  - organizer scope (host inside the shell, organizers only): on the hub the dashboard tour, then the
 *    hub's own tour, each while never seen; then the dashboard card until it is dismissed (or declined
 *    for the session), else the hub card; elsewhere the page's own tour (start, then card);
 *  - site scope (host at the app root): the tour of the route (home page for everyone, logged in or not
 *    — the outcome then lives in the local mirror only —, « My events »).
 * One tour or one card per route and per page view: a tour played or offered on this route hides the
 * card until the route changes (no second card right after the dashboard tour ends on the hub).
 */
export default function useTourInvite(shell, scope, { isDone, isDismissed, markDone }) {
    const auth = useAuth()
    const { pathname } = useLocation()
    const activeId = useTourStore((state) => state.activeId)
    const start = useTourStore((state) => state.start)
    const [welcomeSeen, setWelcomeSeen] = useState(isWelcomeSeen)
    const [invite, setInvite] = useState(null)
    // the route a card was shown on, or a tour started on: no second card there
    const handledOn = useRef(null)

    useEffect(() => {
        const onDone = () => setWelcomeSeen(true)
        window.addEventListener(WELCOME_DONE_EVENT, onDone)
        return () => window.removeEventListener(WELCOME_DONE_EVENT, onDone)
    }, [])

    const isHub = !!shell?.isHub
    const rel = shell?.rel
    const routeKey = scope === 'site' ? pathname : `${shell?.base || ''}/${rel || ''}`

    useEffect(() => {
        if (!activeId) return
        handledOn.current = routeKey
        setInvite(null)
    }, [activeId, routeKey])

    const organizerReady = !!shell?.enabled && !!shell.viewer?.isOrganizer && !!auth.isLoggedIn && !!auth.user?._id
    const ready =
        (scope === 'site' ? !!tourForRoute(pathname) : organizerReady) &&
        !auth.cookiesConsentOpen &&
        welcomeSeen &&
        !activeId

    useEffect(() => {
        // a route already handled keeps its card: the effect re-runs when the account refreshes
        // (`isDone` / `isDismissed` follow `user.systemPreferences.tours`) and must not clear it
        if (handledOn.current === routeKey) return undefined
        setInvite(null)
        if (!ready || !hasConsent()) return undefined

        // a tour the card may offer: not declined for this session, not dismissed for good
        const offerable = (tour) => !!tour && !readAutoAttempted(tour.id) && !isDismissed(tour)
        // a tour never seen starts by itself (first visit), one already seen is offered by the card
        const fresh = (tour) => !!tour && !isDone(tour)
        const dashboard = { def: DASHBOARD_TOUR, firstTarget: '[data-tour="menu-groups"]' }
        const pageTour = tourForPath(rel)
        const page = pageTour && { def: pageTour, firstTarget: firstTargetOf(pageTour, '[data-tour="side-menu"]') }
        let due = null
        if (scope === 'site') {
            const tour = tourForRoute(pathname)
            if (offerable(tour)) due = { def: tour, firstTarget: firstTargetOf(tour) }
        } else if (isHub) {
            if (fresh(DASHBOARD_TOUR)) due = dashboard
            else if (fresh(pageTour)) due = page
            else if (offerable(DASHBOARD_TOUR)) due = dashboard
            else if (offerable(pageTour)) due = page
        } else if (offerable(pageTour)) {
            due = page
        }
        if (!due) return undefined

        let cancelled = false
        const timer = setTimeout(async () => {
            const target = await waitForTarget(due.firstTarget, TARGET_WAIT_MS)
            if (cancelled || !target || isDialogOpen()) return
            handledOn.current = routeKey
            if (fresh(due.def)) start(due.def.id, { auto: true })
            else setInvite(due.def)
        }, SETTLE_MS)
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [ready, scope, pathname, routeKey, isHub, rel, isDone, isDismissed, start])

    const accept = useCallback(() => {
        if (!invite) return
        setInvite(null)
        start(invite.id, { auto: true })
    }, [invite, start])

    const later = useCallback(() => {
        if (!invite) return
        markAutoAttempted(invite.id)
        setInvite(null)
    }, [invite])

    const never = useCallback(() => {
        if (!invite) return
        markAutoAttempted(invite.id)
        setInvite(null)
        markDone(invite, { dismissed: true })
    }, [invite, markDone])

    return { invite, accept, later, never }
}
